// src/pages/PlaySearch.js
import React, { useState, useEffect, useRef, useCallback } from "react";
import axios from "axios";
import "../App.css";
import "../css/PlaySearch.css";
import { FaSearch, FaFilter, FaEdit } from "react-icons/fa";
import { useNavigate, useParams } from "react-router-dom";

export default function PlaySearch() {
  const navigate = useNavigate();
  const { page: pageParam } = useParams();

  const [plays, setPlays] = useState([]);
  const [search, setSearch] = useState("");
  const [genre, setGenre] = useState("");
  const [fundingType, setFundingType] = useState("");
  const [organizationType, setOrganizationType] = useState("");
  const [page, setPage] = useState(parseInt(pageParam) || 1);
  const [totalPages, setTotalPages] = useState(0);
  const [totalResults, setTotalResults] = useState(0);
  const [filterOpen, setFilterOpen] = useState(false);
  const [user, setUser] = useState(null);
  const [userMenuOpen, setUserMenuOpen] = useState(false);
  const [expandedAbstract, setExpandedAbstract] = useState({});
  const [enterCooldown, setEnterCooldown] = useState(false);
  const menuRef = useRef(null);

  const [advancedFilters, setAdvancedFilters] = useState({
    pubDateFrom: "",
    pubDateTo: "",
    subDateFrom: "",
    subDateTo: "",
    minDuration: "",
    maxDuration: "",
    males: "",
    females: "",
    acts: "",
  });

  const API_URL = process.env.REACT_APP_API_URL || "http://localhost:5000";

  const fetchPlays = useCallback(
    async (newPage = 1) => {
      try {
        const response = await axios.get(`${API_URL}/api/plays`, {
          params: {
            search: search.trim() || undefined,
            genre: genre || undefined,
            fundingType: fundingType || undefined,
            organizationType: organizationType || undefined,
            pubDateFrom: advancedFilters.pubDateFrom || undefined,
            pubDateTo: advancedFilters.pubDateTo || undefined,
            subDateFrom: advancedFilters.subDateFrom || undefined,
            subDateTo: advancedFilters.subDateTo || undefined,
            minDuration: advancedFilters.minDuration || undefined,
            maxDuration: advancedFilters.maxDuration || undefined,
            males: advancedFilters.males || undefined,
            females: advancedFilters.females || undefined,
            acts: advancedFilters.acts || undefined,
            page: newPage,
            limit: 10,
          },
          withCredentials: true,
        });

        const data = response.data;
        setPlays(data.plays || []);
        setTotalResults(data.totalResults ?? data.total ?? 0);
        setTotalPages(
          data.totalPages ??
            Math.ceil((data.totalResults ?? data.total ?? 0) / 10)
        );
        setPage(newPage);

        navigate(`/plays/page/${newPage}`, { replace: true });
      } catch (error) {
        console.error("Error fetching plays:", error);
        setPlays([]);
        setTotalResults(0);
        setTotalPages(0);
        if (error.response?.status === 401)
          navigate("/login", { replace: true });
      }
    },
    [search, genre, fundingType, organizationType, advancedFilters, navigate, API_URL]
  );

  const checkAuth = useCallback(async () => {
    try {
      const res = await axios.get(`${API_URL}/api/users/profile`, {
        withCredentials: true,
      });
      if (!res.data.user) throw new Error("Not authenticated");
      setUser(res.data.user);
    } catch (err) {
      navigate("/login", { replace: true });
    }
  }, [navigate, API_URL]);

  useEffect(() => {
    document.title = "MPDB";
    checkAuth();
    fetchPlays(parseInt(pageParam) || 1);
  }, [fetchPlays, checkAuth, pageParam]);

  useEffect(() => {
    const handleGlobalEnter = (e) => {
      if (e.key === "Enter" && !enterCooldown) {
        fetchPlays(1);
        setEnterCooldown(true);
        setTimeout(() => setEnterCooldown(false), 1000);
      }
    };
    document.addEventListener("keydown", handleGlobalEnter);
    return () => document.removeEventListener("keydown", handleGlobalEnter);
  }, [enterCooldown, fetchPlays]);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target))
        setUserMenuOpen(false);
    };
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const firstLetter = user?.firstName?.charAt(0) || "";
  const restName = user ? `${user.firstName.slice(1)} ${user.lastName}` : "";

  const handleLogout = async () => {
    try {
      await axios.post(`${API_URL}/api/auth/logout`, {}, { withCredentials: true });
      setUser(null);
      navigate("/login", { replace: true });
    } catch (err) {
      console.error("Logout failed:", err);
    }
  };

  const toggleAbstract = (id) =>
    setExpandedAbstract((prev) => ({ ...prev, [id]: !prev[id] }));

  const handleAdvancedChange = (e) => {
    const { name, value } = e.target;
    setAdvancedFilters((prev) => ({ ...prev, [name]: value }));
  };

  const handleSearchClick = () => {
    if (!enterCooldown) {
      fetchPlays(1);
      setEnterCooldown(true);
      setTimeout(() => setEnterCooldown(false), 1000);
    }
  };

  const handleSearchKeyPress = (e) => {
    if (e.key === "Enter" && !enterCooldown) {
      fetchPlays(1);
      setEnterCooldown(true);
      setTimeout(() => setEnterCooldown(false), 1000);
    }
  };

  const handlePageChange = (newPage) => {
    if (newPage >= 1 && newPage <= totalPages && newPage !== page) fetchPlays(newPage);
  };

  const resetFilters = () => {
    setSearch("");
    setGenre("");
    setFundingType("");
    setOrganizationType("");
    setAdvancedFilters({
      pubDateFrom: "",
      pubDateTo: "",
      subDateFrom: "",
      subDateTo: "",
      minDuration: "",
      maxDuration: "",
      males: "",
      females: "",
      acts: "",
    });
    fetchPlays(1);
  };

  const shownResults = Math.min(page * 10, totalResults);

  const handlePlaySample = async (play) => {
    try {
      const response = await axios.get(`${API_URL}/api/plays/sample/${play._id}`, {
        responseType: 'blob',
        withCredentials: true,
      });

      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement("a");
      link.href = url;
      link.setAttribute("download", play.playFile?.filename || `${play.title}-sample.pdf`);
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
    } catch (err) {
      console.error("Error fetching play sample:", err);
      alert(
        err.response?.status === 404
          ? "No available play sample for this play."
          : "Error fetching play sample"
      );
    }
  };

  return (
    <div className="play-search-wrapper">
      <header className="mpdb-header">
        <div className="search-user-container">
          {user && user.account === 4 && (
            <button
              className="admin-btn"
              onClick={() => navigate("/admin/users")}
            >
              Administration
            </button>
          )}

          <div className="search-bar">
            <input
              type="text"
              placeholder="Search Title / Abstract / Author (First + Last)"
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              onKeyDown={handleSearchKeyPress}
            />
            <button onClick={handleSearchClick}><FaSearch /></button>
          </div>

          {user && (
            <div className="user-section1" ref={menuRef}>
              <div
                className="user-icon-circle"
                onClick={() => setUserMenuOpen(!userMenuOpen)}
              >
                {firstLetter}
              </div>
              <span onClick={() => setUserMenuOpen(!userMenuOpen)}>{restName}</span>
              {userMenuOpen && (
                <div className="user-dropdown">
                  {user.account >= 2 && (
                    <button onClick={() => navigate(`/profile/${user._id}`)}>Profile</button>
                  )}
                  <button onClick={() => navigate("/settings")}>Settings</button>
                  <button onClick={handleLogout}>Logout</button>
                </div>
              )}
            </div>
          )}
        </div>
      </header>

      <div className="search-filters">
        <select value={genre} onChange={(e) => setGenre(e.target.value)}>
          <option value="">Genre</option>
          <option value="Drama">Drama</option>
          <option value="Comedy">Comedy</option>
          <option value="Musical">Musical</option>
          <option value="Reader’s Theatre">Reader’s Theatre</option>
          <option value="Monologue">Monologue</option>
          <option value="Tragedy">Tragedy</option>
          <option value="Mystery">Mystery</option>
        </select>

        <select value={fundingType} onChange={(e) => setFundingType(e.target.value)}>
          <option value="">Funding Type</option>
          <option value="Paid">Paid</option>
          <option value="Donated">Donated</option>
        </select>

        <select value={organizationType} onChange={(e) => setOrganizationType(e.target.value)}>
          <option value="">Organization Type</option>
          <option value="Elementary">Elementary</option>
          <option value="Middle School">Middle School</option>
          <option value="High School">High School</option>
          <option value="University">University</option>
          <option value="Community">Community</option>
        </select>

        <div className="filter-wrapper">
          <button
            type="button"
            className="filter-btn"
            onClick={() => setFilterOpen(!filterOpen)}
          >
            <FaFilter /> More Filters
          </button>

          {filterOpen && (
            <div className="filter-dropdown expanded-large">
              <button className="reset-filters-btn" onClick={resetFilters}>Reset</button>
              
              <h3>Advanced Filters</h3>
              <div className="advanced-grid">
                <div className="filter-section">
                  <h4>Publication Dates</h4>
                  <label>From:</label>
                  <input type="date" name="pubDateFrom" value={advancedFilters.pubDateFrom} onChange={handleAdvancedChange} />
                  <label>To:</label>
                  <input type="date" name="pubDateTo" value={advancedFilters.pubDateTo} onChange={handleAdvancedChange} />
                </div>
                <div className="filter-section">
                  <h4>Submission Dates</h4>
                  <label>From:</label>
                  <input type="date" name="subDateFrom" value={advancedFilters.subDateFrom} onChange={handleAdvancedChange} />
                  <label>To:</label>
                  <input type="date" name="subDateTo" value={advancedFilters.subDateTo} onChange={handleAdvancedChange} />
                </div>
                <div className="filter-section">
                  <h4>Duration (Minutes)</h4>
                  <label>Min:</label>
                  <input type="number" name="minDuration" placeholder="Min" value={advancedFilters.minDuration} onChange={handleAdvancedChange} />
                  <label>Max:</label>
                  <input type="number" name="maxDuration" placeholder="Max" value={advancedFilters.maxDuration} onChange={handleAdvancedChange} />
                </div>
                <div className="filter-section">
                  <h4>Cast Requirements</h4>
                  <label>Men:</label>
                  <input type="number" name="males" placeholder="Men" value={advancedFilters.males} onChange={handleAdvancedChange} />
                  <label>Women:</label>
                  <input type="number" name="females" placeholder="Women" value={advancedFilters.females} onChange={handleAdvancedChange} />
                </div>
                <div className="filter-section">
                  <h4>Acts</h4>
                  <label>Number of Acts:</label>
                  <input type="number" name="acts" placeholder="e.g. 3" value={advancedFilters.acts} onChange={handleAdvancedChange} />
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {totalPages > 1 && (
        <div className="pagination bottom-pagination">
          <button onClick={() => handlePageChange(page - 1)} disabled={page === 1}>Previous</button>
          {[...Array(totalPages).keys()].map((num) => (
            <button key={num} onClick={() => handlePageChange(num + 1)} className={page === num + 1 ? "active" : ""}>
              {num + 1}
            </button>
          ))}
          <button onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>Next</button>
        </div>
      )}

      <p className="results-count">
        Showing {shownResults} of {totalResults} results
      </p>

      <div className="play-results">
        {plays.map((play, index) => {
          const canEdit = user && (user._id === play.authorId || user.account === 4);
          return (
            <React.Fragment key={play._id}>
              <div className="play-card">
                <img src={play.coverImage || "/placeholder.png"} alt={play.title} />
                <div className="play-info">
                  <div className="title-edit-row">
                    <h2>{play.title}</h2>
                    {canEdit && (
                      <button
                        className="edit-play-btn"
                        onClick={() => navigate(`/edit-play/${play._id}`)}
                        title="Edit Play"
                      >
                        <FaEdit /> Edit
                      </button>
                    )}
                  </div>

                  <p className="play-author">
                    by{" "}
                    {play.authorId ? (
                      <span
                        className="author-link"
                        onClick={() => navigate(`/profile/${play.authorId}`)}
                        style={{ cursor: "pointer", textDecoration: "underline", color: "#007bff" }}
                      >
                        {play.author}
                      </span>
                    ) : (
                      play.author || "Anonymous"
                    )}
                  </p>

                  <p className="play-details">
                    {play.genre || "-"} | {play.duration || 0} Minutes | {play.males || 0} M, {play.females || 0} W | {play.total || 0} Total Actors | {play.organizationType || "-"}
                  </p>

                  <p className="play-meta">
                    {play.funding || "-"} | {play.acts || "-"} Acts | Publication Date: {play.publicationDate ? new Date(play.publicationDate).toLocaleDateString() : "-"} | Submission Date: {play.submissionDate ? new Date(play.submissionDate).toLocaleDateString() : "-"}
                  </p>

                  <p className={`play-abstract ${expandedAbstract[play._id] ? "expanded" : ""}`}>
                    {play.abstract || "No abstract available"}
                  </p>
                  {play.abstract && play.abstract.length > 200 && (
                    <button className="read-more-btn" onClick={() => toggleAbstract(play._id)}>
                      {expandedAbstract[play._id] ? "Read Less" : "Read More"}
                    </button>
                  )}

                  <div className="play-actions">
                    <button
                      className="request-sample-btn"
                      onClick={() => {
                        if (window.confirm("Are you sure you want to download this play sample?")) {
                          handlePlaySample(play);
                        }
                      }}
                    >
                      Request Play Sample
                    </button>
                  </div>
                </div>
              </div>
              {index < plays.length - 1 && <hr className="play-divider" />}
            </React.Fragment>
          );
        })}
      </div>

      {totalPages > 1 && (
        <div className="pagination bottom-pagination">
          <button onClick={() => handlePageChange(page - 1)} disabled={page === 1}>Previous</button>
          {[...Array(totalPages).keys()].map((num) => (
            <button key={num} onClick={() => handlePageChange(num + 1)} className={page === num + 1 ? "active" : ""}>
              {num + 1}
            </button>
          ))}
          <button onClick={() => handlePageChange(page + 1)} disabled={page === totalPages}>Next</button>
        </div>
      )}

      {user && (user.account === 3 || user.account === 4) && (
        <div
          className="floating-create-play-btn"
          onClick={() => navigate("/plays/create")}
          title="Create Play"
        >
          <img src="/bookimage.png" alt="Create Play" />
        </div>
      )}
    </div>
  );
}
